import { Controller } from "@hotwired/stimulus"

// The chat panel beside a profile (_chat_panel). It wears the Messages
// sidebar's frame and views - docked sidebar (the default), drawer, floating
// window, full screen - and owns only where it is and whether it is open;
// profile_messages_controller fills it. Docked, html.profile-chat-docked
// makes room for it (_profile_page): between the feed and a docked avatar,
// or to the right of the feed otherwise. One right sidebar at a time, so it
// and the app's Messages sidebar close each other.
const VIEWS = ["sidebar", "drawer", "floating"]
const VIEW_KEY = "emailpa-profile-chat-view"

export default class extends Controller {
  static targets = ["panel", "fullscreen"]
  static values = { chatUrl: String, profileUrl: String, open: Boolean }

  connect() {
    this._applyView(this._savedView())
    this._onKeydown = (event) => this._keydown(event)
    document.addEventListener("keydown", this._onKeydown)
    // The app's Messages sidebar opening takes the right side back.
    this._onPanelsChanged = () => {
      if (this._isOpen() && document.querySelector(".messages-drawer[data-messages-sidebar-target='panel']:not(.hidden)")) this.close()
    }
    window.addEventListener("jane:panels-changed", this._onPanelsChanged)
    // After the page's other controllers (profile-messages) have connected.
    if (this.openValue) this._openOnLoad = setTimeout(() => this._openFromAddress(), 0)
  }

  disconnect() {
    clearTimeout(this._openOnLoad)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("jane:panels-changed", this._onPanelsChanged)
    document.documentElement.classList.remove("profile-chat-docked")
  }

  // Opened at /u/:slug/messages: the conversation, as Message would.
  _openFromAddress() {
    const messages = this.application.getControllerForElementAndIdentifier(this.element, "profile-messages")
    if (messages) messages.selectMessagesTab()
    else this.open()
  }

  open(event) {
    event?.preventDefault?.()
    if (!this.hasPanelTarget) return
    const wasOpen = this._isOpen()
    if (!wasOpen) window.dispatchEvent(new CustomEvent("jane:messages-close"))
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.setAttribute("aria-hidden", "false")
    this._syncDocked()
    if (this.chatUrlValue && location.pathname !== new URL(this.chatUrlValue, location.href).pathname) {
      history.pushState({}, "", this.chatUrlValue)
    }
    if (!wasOpen) window.dispatchEvent(new CustomEvent("profile-chat:opened"))
  }

  close() {
    if (!this.hasPanelTarget || !this._isOpen()) return
    this._setFullscreen(false)
    this.panelTarget.classList.add("hidden")
    this.panelTarget.setAttribute("aria-hidden", "true")
    this._syncDocked()
    if (this.chatUrlValue && location.pathname === new URL(this.chatUrlValue, location.href).pathname) {
      history.pushState({}, "", this.profileUrlValue)
    }
    window.dispatchEvent(new CustomEvent("profile-chat:closed"))
  }

  setView(event) {
    const view = event.currentTarget.dataset.view
    this._applyView(view)
    try { window.localStorage.setItem(VIEW_KEY, view) } catch (_error) { /* private mode */ }
  }

  toggleFullscreen(event) {
    event?.preventDefault()
    this._setFullscreen(!this._fullscreen)
  }

  // Drag the floating window by its header.
  startDrag(event) {
    if (this._view !== "floating" || this._fullscreen || event.button !== 0) return
    if (event.target.closest("button, a, input, textarea")) return
    const panel = this.panelTarget
    const rect = panel.getBoundingClientRect()
    const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    panel.style.transform = "none"
    panel.style.right = "auto"
    panel.style.left = `${rect.left}px`
    panel.style.top = `${rect.top}px`
    const move = (moved) => {
      panel.style.left = `${Math.max(0, Math.min(innerWidth - rect.width, moved.clientX - offset.x))}px`
      panel.style.top = `${Math.max(0, Math.min(innerHeight - rect.height, moved.clientY - offset.y))}px`
    }
    const up = () => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", up)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", up)
  }

  _isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  _keydown(event) {
    if (event.key !== "Escape" || !this._isOpen()) return
    if (document.documentElement.classList.contains("profile-twin-fullscreen")) return
    if (event.target.closest?.(".assistant-header-menu-panel.is-open")) return
    if (this._fullscreen) return this._setFullscreen(false)
    const history = this.panelTarget.querySelector("#profile-chat-history:not([hidden])")
    if (history) {
      this.application.getControllerForElementAndIdentifier(this.element, "profile-messages")?.toggleHistory()
      return
    }
    if (event.target.matches?.("input, textarea") && event.target.value) return
    this.close()
  }

  _savedView() {
    try {
      const saved = window.localStorage.getItem(VIEW_KEY)
      return VIEWS.includes(saved) ? saved : "sidebar"
    } catch (_error) {
      return "sidebar"
    }
  }

  _applyView(view) {
    if (!this.hasPanelTarget) return
    this._view = VIEWS.includes(view) ? view : "sidebar"
    VIEWS.forEach((mode) => this.panelTarget.classList.toggle(`messages-drawer--${mode}`, mode === this._view))
    ;["top", "left", "right", "transform"].forEach((property) => { this.panelTarget.style[property] = "" })
    this.panelTarget.querySelectorAll(".messages-view-panel [data-view]").forEach((item) => {
      item.classList.toggle("is-active", item.dataset.view === this._view)
    })
    this._syncDocked()
  }

  _setFullscreen(on) {
    if (!this.hasPanelTarget) return
    this._fullscreen = on
    this.panelTarget.classList.toggle("messages-drawer--fullscreen", on)
    if (!this.hasFullscreenTarget) return
    const label = on ? "Exit full screen" : "Full screen"
    this.fullscreenTarget.setAttribute("aria-pressed", String(on))
    this.fullscreenTarget.title = label
    this.fullscreenTarget.setAttribute("aria-label", label)
    const icon = this.fullscreenTarget.querySelector("i")
    if (icon) icon.className = `bi ${on ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`
  }

  _syncDocked() {
    const docked = this._view === "sidebar" && this._isOpen()
    document.documentElement.classList.toggle("profile-chat-docked", docked)
    window.dispatchEvent(new CustomEvent("jane:panels-changed"))
  }
}
